import { arrayToMap } from "@excalidraw/common";
import { isTextElement, syncMovedIndices } from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import { collectElementIds } from "./layerTree";

import type { LayerNode } from "./layerTree";

export type DropTarget = {
  /** node the pointer is over */
  nodeId: string;
  /** above = becomes the row visually above target (higher z), below =
      under it, inside = child of a group/frame row */
  position: "above" | "below" | "inside";
};

const findNode = (nodes: LayerNode[], id: string): LayerNode | null => {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }
    const hit = findNode(node.children, id);
    if (hit) {
      return hit;
    }
  }
  return null;
};

/** parent chain of a node: frameId + groupIds (deepest→shallowest) */
const findParentChain = (
  nodes: LayerNode[],
  id: string,
  frameId: string | null = null,
  groupIds: string[] = [],
): { frameId: string | null; groupIds: string[] } | null => {
  for (const node of nodes) {
    if (node.id === id) {
      return { frameId, groupIds };
    }
    const nextFrame = node.kind === "frame" ? node.id : frameId;
    const nextGroups =
      node.kind === "group" ? [node.id, ...groupIds] : groupIds;
    const hit = findParentChain(node.children, id, nextFrame, nextGroups);
    if (hit) {
      return hit;
    }
  }
  return null;
};

/**
 * Computes the next scene array (bottom→top) for dropping the dragged
 * layer-tree nodes at the target, reparenting frameId/groupIds and
 * re-syncing fractional indices of the moved elements.
 * Returns null for no-op/illegal drops (e.g. a node into its own subtree).
 */
export const computeDropResult = (
  elements: readonly ExcalidrawElement[],
  tree: LayerNode[],
  draggedNodeIds: string[],
  target: DropTarget,
): ExcalidrawElement[] | null => {
  const draggedNodes = draggedNodeIds
    .map((id) => findNode(tree, id))
    .filter((node): node is LayerNode => node != null);
  if (!draggedNodes.length) {
    return null;
  }

  // illegal: target inside a dragged subtree (or the drag itself)
  const targetInsideDragged = draggedNodes.some(
    (node) =>
      node.id === target.nodeId ||
      findNode(node.children, target.nodeId) != null,
  );
  if (targetInsideDragged) {
    return null;
  }

  const targetNode = findNode(tree, target.nodeId);
  if (!targetNode) {
    return null;
  }

  const draggedElementIds = new Set(
    draggedNodes.flatMap((node) => collectElementIds(node)),
  );
  // bound text rides along with its container
  for (const element of elements) {
    if (
      isTextElement(element) &&
      element.containerId &&
      draggedElementIds.has(element.containerId)
    ) {
      draggedElementIds.add(element.id);
    }
  }

  // destination parent chain (frame + group nesting) for the moved roots
  let destination: { frameId: string | null; groupIds: string[] };
  if (target.position === "inside") {
    const chain = findParentChain(tree, target.nodeId)!;
    destination =
      targetNode.kind === "frame"
        ? { frameId: targetNode.id, groupIds: [] }
        : targetNode.kind === "group"
        ? {
            frameId: chain.frameId,
            groupIds: [targetNode.id, ...chain.groupIds],
          }
        : chain; // "inside" a plain element row = same parent as that row
  } else {
    destination = findParentChain(tree, target.nodeId)!;
  }

  // dragging a group node keeps its internal nesting: members keep the
  // groupIds up to (and including) the dragged group, then get the
  // destination chain appended
  const draggedGroupIds = new Set(
    draggedNodes.filter((node) => node.kind === "group").map((node) => node.id),
  );

  const rewriteParents = (element: ExcalidrawElement): ExcalidrawElement => {
    if (isTextElement(element) && element.containerId) {
      // bound text mirrors its container's frame; groupIds untouched
      return { ...element, frameId: destination.frameId };
    }
    const draggedRootIndex = element.groupIds.findIndex((gid) =>
      draggedGroupIds.has(gid),
    );
    const keptInternal =
      draggedRootIndex === -1
        ? []
        : element.groupIds.slice(0, draggedRootIndex + 1);
    return {
      ...element,
      groupIds: [...keptInternal, ...destination.groupIds],
      frameId: destination.frameId,
    };
  };

  // anchor element in scene (bottom→top) order:
  // panel "above" = higher z = inserted AFTER the anchor in scene order
  const targetElementIds = new Set(collectElementIds(targetNode));
  const anchor =
    target.position === "below"
      ? elements.find((el) => targetElementIds.has(el.id)) // lowest z
      : [...elements].reverse().find((el) => targetElementIds.has(el.id)); // highest z
  if (!anchor) {
    return null;
  }

  const remaining = elements.filter((el) => !draggedElementIds.has(el.id));
  const moved = elements
    .filter((el) => draggedElementIds.has(el.id))
    .map(rewriteParents);

  const anchorIndex = remaining.findIndex((el) => el.id === anchor.id);
  const insertAt = target.position === "below" ? anchorIndex : anchorIndex + 1;
  const next = [
    ...remaining.slice(0, insertAt),
    ...moved,
    ...remaining.slice(insertAt),
  ];
  return syncMovedIndices(next, arrayToMap(moved));
};
